import { ButtonBase, Text } from '@belong/ui';
import clsx from 'clsx';
import { UnitListingDetailsModel } from 'src/api/models';
import {
  AvailabilitySignals,
  isTourable,
  showsComingSoonTreatment,
} from 'src/features/listing-page/availability';
import { useUser } from 'src/features/users/useUser';
import { HOUSE_PAGE_STRINGS } from '../../house-page.strings';
import { isEstimatedShowReadyOnInFuture, isSelfTourOffered } from '../../utils';
import { useBookedTours, useGetResidentTourRequests } from '../../queries';
import css from './schedule-tour.module.css';

type ScheduleTourProps = {
  home: UnitListingDetailsModel;
  onClick: () => void;
};

export function ScheduleTour({ home, onClick }: ScheduleTourProps) {
  const { basicInfo, listingInfo } = home;
  const {
    isOpenHouseAllowed,
    estimatedShowReadyOn,
    // `!`: this renders inside the house page, which already destructured
    // `listingInfo` unconditionally here.
  } = listingInfo!;

  const availabilitySignals: AvailabilitySignals = listingInfo || {};

  const disabled = !isTourable({
    currentMilestone: basicInfo?.currentMilestone,
    ...listingInfo,
  });

  const selfTourOffered = isSelfTourOffered(listingInfo);
  const showSubtitle = !disabled && (selfTourOffered || isOpenHouseAllowed);
  const subtitleText = selfTourOffered
    ? HOUSE_PAGE_STRINGS['tour.selfTourAvailable']
    : HOUSE_PAGE_STRINGS['tour.seeTimes'];

  const showComingSoonReason =
    disabled && showsComingSoonTreatment(availabilitySignals);
  // The reason line only adds a date ("Starting {date}") on top of the "Touring Soon" title, so
  // it's only worth showing when the estimate is still a real future date — a stale/missing
  // estimate just leaves "Touring Soon" on its own rather than a reason line with nothing useful
  // to say.
  const showComingSoonReasonText =
    showComingSoonReason &&
    !!estimatedShowReadyOn &&
    isEstimatedShowReadyOnInFuture(estimatedShowReadyOn);
  const comingSoonReasonText = showComingSoonReasonText
    ? HOUSE_PAGE_STRINGS['tour.comingSoonCtaReason'].replace(
        '{date}',
        new Date(`${estimatedShowReadyOn}T12:00:00`).toLocaleDateString(
          'en-US',
          { month: 'long', day: 'numeric', year: 'numeric' },
        ),
      )
    : undefined;

  const { user } = useUser();
  const { userId } = user || {};

  const { data: tourRequests } = useGetResidentTourRequests({
    enabled: !!userId,
  });
  const { bookedTours } = useBookedTours(
    { homeId: basicInfo?.unitId! },
    { enabled: !!userId },
  );

  const bookedTour = bookedTours?.find(({ tourAttendee }) =>
    ['Booked', 'Invited'].includes(tourAttendee!.attendeeStatus),
  );
  const isTourRequestAlreadyMade = tourRequests?.some(
    (tourRequest) =>
      tourRequest.homeUniqueId === home.basicInfo?.unitId &&
      [
        'Submitted',
        'SchedulingInProgress',
        'OfferSent',
        'ManualScheduleRequired',
      ].includes(tourRequest.status),
  );
  const titleText =
    HOUSE_PAGE_STRINGS[
      showComingSoonReason
        ? 'tour.touringSoon'
        : bookedTour?.tour?.type === 'OpenHome' ||
            bookedTour?.tour?.type === 'SelfTour'
          ? 'tour.rescheduleTour'
          : bookedTour?.tour?.type === 'PrivateTour'
            ? 'tour.cancelTour'
            : isTourRequestAlreadyMade
              ? 'tour.editAvailability'
              : 'tour.tourThisHome'
    ];

  return (
    <ButtonBase
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        css.tourButton,
        'flex flex-row items-center justify-between xl:flex-col xl:justify-center',
        'transition-colors duration-hover',
        {
          'border-navy xl:hover:border-2 xl:hover:border-dark-navy': !disabled,
        },
        { 'border-gray text-gray': disabled },
        'rounded border-0 border-solid xl:border',
      )}
    >
      <Text fontWeight="semibold">{titleText}</Text>
      {showSubtitle && (
        <Text fontWeight="semibold" className="text-green xl:text-p1">
          {subtitleText}
        </Text>
      )}
      {showComingSoonReasonText && (
        <Text fontWeight="semibold" className="text-gray xl:text-p1">
          {comingSoonReasonText}
        </Text>
      )}
    </ButtonBase>
  );
}
